import React, { useState, useEffect, FC, useRef } from 'react'
import { InView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'

import {
  ActivitiesContainer,
  LoadingMoreActivitiesIndicator,
  Wrapper,
} from './Activities.styles'
import {
  isComment,
  isUnreadReply,
  isRevision,
  isRevisionDeleted,
  isRevisionUnavailable,
  Activity,
} from '../../utils'
import { CommentProps } from 'components/Comment'
import ActivityComment from '../ActivityComment'
import ActivityRevision from '../ActivityRevision'
import { ActivityRevisionDeleted } from '../ActivityRevisionDeleted'
import { ActivityRevisionUnavailable } from '../ActivityRevisionUnavailable'

import { useDelayedMarkCommentsOrRepliesAsRead } from '../../operations'
import lazyWithPreload from 'utils/lazyWithPreload'
import usePrevious from 'utils/hooks/usePrevious'

import { ArtboardInSidebar } from 'modules/shares/types'
import { CommentInfoFragment, GetActivityFeedQuery } from 'gqlTypes'

const Comment = lazyWithPreload(
  () => import('components/Comment') as any
) as FC<CommentProps>

/**
 * TYPES
 */
interface ScrollInfo {
  scrollPosition: number
  scrollAtBottom: boolean
}

interface ActivitiesProps {
  activities: GetActivityFeedQuery['activityFeed']['entries']
  artboard: ArtboardInSidebar
  shareIdentifier: string
  scrollToBottom: () => void
  setScrollTop: (scrollPosition: number) => void
  getScrollInfo: () => ScrollInfo
  onLoadMore: () => Promise<void>
  userCanAccessVersions: boolean
  activityToShow: string
}

/**
 * COMPONENTS
 */
export const Activities = ({
  activities = [],
  scrollToBottom,
  setScrollTop,
  getScrollInfo,
  artboard,
  shareIdentifier,
  userCanAccessVersions,
  onLoadMore,
  activityToShow,
}: ActivitiesProps) => {
  const { permanentArtboardShortId, documentVersionShortId } = artboard

  const location = useLocation()

  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [renderScrollTrigger, setRenderScrollTrigger] = useState<boolean>(false)
  const firstLoad = useRef<boolean>(true)

  const previousActivities = (usePrevious(activities) ||
    []) as typeof activities
  const previousActivityToShow = usePrevious(activityToShow) || ''
  const previousScrollInfo = usePrevious(getScrollInfo()) || {
    scrollPosition: 0,
    scrollAtBottom: false,
  }

  // Marks comments as read after 2 seconds
  useDelayedMarkCommentsOrRepliesAsRead({
    activities,
    permanentArtboardShortId,
    shareIdentifier,
  })

  /**
   * Scroll behavior
   *
   * - On load, if "comment" param is on the URL, scroll to this comment
   * - When returning from thread panel, the parent comment should be at the
   *   top (previousActivityToShow prop)
   * - When sending a new message, should scroll to the bottom
   * - When paginating, should retain the scroll position
   */
  const handleScrollRestoration = () => {
    const { scrollPosition, scrollAtBottom } = previousScrollInfo

    const url = new URLSearchParams(location.search)
    const commentAnchor = url.get('comment')

    // After clicking a comment notification
    if (commentAnchor && firstLoad.current) {
      window.requestAnimationFrame(() => {
        document.getElementById(commentAnchor)?.scrollIntoView()
      })
      firstLoad.current = false
      return
    }

    // Scroll to a specific comment after going back from a thread
    if (previousActivityToShow) {
      window.requestAnimationFrame(() => {
        document.getElementById(previousActivityToShow)?.scrollIntoView()
      })
      return
    }

    // Scroll to bottom after activities have been fetched
    if (previousActivities.length === 0 && activities.length > 0) {
      return scrollToBottom()
    }

    // Programatically trigger scroll to bottom
    if (scrollAtBottom) {
      return scrollToBottom()
    }

    const comments = activities.filter(isComment)

    // WWhen there are just one or fewer comments, the scroll has its
    // default behaviour
    if (comments.length <= 1) return

    const lastComment = comments[comments.length - 1]

    const previousComments = previousActivities.filter(isComment)
    const previousLastComment = previousComments[previousComments.length - 1]

    const lastCommentChanged =
      lastComment.identifier !== previousLastComment.identifier

    const hasNewComment =
      comments.length > previousComments.length && lastCommentChanged

    // Scroll to bottom after a new own comment has been added
    const hasNewCommentByCurrentUser =
      hasNewComment && lastComment.userIsCreator

    if (hasNewCommentByCurrentUser) {
      return scrollToBottom()
    }

    // Restore scroll position after pagination
    const hasPagination =
      activities.length > previousActivities.length && !lastCommentChanged

    if (hasPagination) {
      setScrollTop(scrollPosition)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      handleScrollRestoration()

      // Render InView component only after the first activities render,
      // otherwise it will trigger pagination
      if (!renderScrollTrigger) setRenderScrollTrigger(true)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, activityToShow])

  // Fetch previous comments when scroll top
  const handleObserverChange = (inView: boolean) => {
    if (!inView) return

    setIsLoadingMore(true)

    onLoadMore().then(() => {
      setIsLoadingMore(false)
    })
  }

  return (
    <Wrapper data-testid="activities">
      {isLoadingMore && <LoadingMoreActivitiesIndicator />}

      {renderScrollTrigger && (
        <InView onChange={handleObserverChange}>{null}</InView>
      )}

      <ActivitiesContainer>
        {activities.map((activity: Activity) => {
          if (isComment(activity)) {
            const replies = activity.replies as CommentInfoFragment[]
            const repliesLength = replies?.length
            const lastReply = replies?.[0]

            return (
              <ActivityComment
                identifier={activity.identifier}
                isEdited={!!activity.isEdited}
                isRead={!!activity.isRead}
                body={activity.body!}
                user={activity.user}
                createdAt={activity.createdAt!}
                updatedAt={activity.updatedAt!}
                userIsCreator={activity.userIsCreator}
                userCanDelete={activity.userCanDelete}
                Comment={Comment}
                key={activity.identifier}
                permanentArtboardShortId={permanentArtboardShortId}
                documentVersionShortId={documentVersionShortId}
                shareIdentifier={shareIdentifier}
                repliesMeta={
                  repliesLength
                    ? {
                        count: repliesLength,
                        lastReplyDate: lastReply.createdAt!,
                        unread: replies.some(isUnreadReply),
                      }
                    : undefined
                }
              />
            )
          }

          if (isRevision(activity)) {
            return (
              <ActivityRevision
                key={activity.identifier}
                artboard={activity.artboard}
                createdAt={activity.createdAt!}
                createdAfterVersioning={activity.createdAfterVersioning!}
                userIsCreator={activity.userIsCreator}
                creator={activity.creator}
                userCanAccessVersions={userCanAccessVersions}
                displayAsEnabled={
                  artboard.revisionIdentifier === activity.identifier
                }
                isActive={artboard.revisionIdentifier === activity.identifier}
                kind={activity.kind}
              />
            )
          }

          if (isRevisionDeleted(activity)) {
            return (
              <ActivityRevisionDeleted
                key={activity.identifier}
                revision={activity}
              />
            )
          }

          if (isRevisionUnavailable(activity)) {
            return (
              <ActivityRevisionUnavailable
                key={activity.identifier}
                revision={activity}
              />
            )
          }

          return null
        })}
      </ActivitiesContainer>
    </Wrapper>
  )
}
